"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FixedDelayStrategy = exports.NoDelayStrategy = exports.RandomDelayStrategy = exports.ExponentialDelayStrategy = exports.Retry = void 0;
/**
 * Retry handler to enable different retry strategies upon handled failures.
 */
class Retry {
    constructor(strategy, logger, maxAttempts = 3) {
        if (maxAttempts <= 0) {
            throw new Error("A valid positive number must be supplied for maximum attempts.");
        }
        this.strategy = strategy;
        this.maxAttempts = maxAttempts;
        this.logger = logger;
    }
    /**
     * Retries an operation a specified number of times at intervals based on the retry strategy.
     * @param operation The operation to retry.
     * @param shouldRetryOnError A function to determine if the operation should retry based on an error, if not passed then it WILL retry on any errors.
     * @param shouldRetryOnResult A function to determine if the operation should retry based on a result, if not passed then it WILL NOT retry when a result is received.
     * @returns The result of the operation.
     */
    execute(operation, shouldRetryOnError, shouldRetryOnResult) {
        return __awaiter(this, void 0, void 0, function* () {
            let attempt = 0;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                attempt++;
                try {
                    const result = yield operation();
                    if (attempt < this.maxAttempts && shouldRetryOnResult && shouldRetryOnResult(result)) {
                        continue;
                    }
                    return result;
                }
                catch (error) {
                    if (attempt >= this.maxAttempts) {
                        this.logger.error("Retry operation failed after {0} retries.", attempt);
                        throw error;
                    }
                    //We only throw if the "shouldRetryOnError" function has been provided and decides that it shouldn't be retried.
                    if (shouldRetryOnError && !shouldRetryOnError(error)) {
                        throw error;
                    }
                    const delay = this.strategy.getNextDelay(attempt);
                    this.logger.debug(`Retry operation.  Attempt '${attempt}' with delay '${delay}ms'.`);
                    yield this.timeout(delay);
                }
            }
        });
    }
    timeout(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
exports.Retry = Retry;
/**
 * A retry strategy which implements an exponential wait between attempts.
 */
class ExponentialDelayStrategy {
    constructor(minimumDelay = 500, maximumDelay = 15000) {
        this.deltaDelay = 500;
        this.minimumDelay = minimumDelay;
        this.maximumDelay = maximumDelay;
    }
    /**
     * Returns the next delay before retrying again (in milliseconds).
     * @param attempt the attempt number.
     * @returns the amount to delay in milliseconds.
     */
    getNextDelay(attempt) {
        return Math.min(this.minimumDelay + (Math.pow(2.0, attempt) - 1.0) * this.deltaDelay, this.maximumDelay);
    }
}
exports.ExponentialDelayStrategy = ExponentialDelayStrategy;
/**
 * A retry strategy which implements an exponential wait between attempts.
 */
class RandomDelayStrategy {
    constructor(minimumDelay = 500, maximumDelay = 15000) {
        this.minimumDelay = minimumDelay;
        this.maximumDelay = maximumDelay;
    }
    /**
     * Returns the next delay before retrying again (in milliseconds).
     * @param attempt the attempt number.
     * @returns the amount to delay in milliseconds.
     */
    getNextDelay() {
        return Math.max(this.minimumDelay, Math.random() * this.maximumDelay);
    }
}
exports.RandomDelayStrategy = RandomDelayStrategy;
/**
 * A retry strategy with no wait between attempts.
 */
class NoDelayStrategy {
    /**
     * Returns a zero delay.
     */
    getNextDelay() {
        return 0;
    }
}
exports.NoDelayStrategy = NoDelayStrategy;
/**
 * A retry strategy with a fixed delay between attempts.
 */
class FixedDelayStrategy {
    /**
     * @param delay the fixed delay between attempts in milliseconds.
     */
    constructor(delay) {
        this.delay = delay;
    }
    /**
     * Returns the next delay before retrying again (in milliseconds).
     * @returns the amount to delay in milliseconds.
     */
    getNextDelay() {
        return this.delay;
    }
}
exports.FixedDelayStrategy = FixedDelayStrategy;
